import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import type React from 'react';

import type { IconTypes } from '~/components/Icon/IconTypes';
import { useStores } from '~/hooks/useStore';
import type { Category, Product } from '~/stores/CategoriesStore';
import { SEARCH_MIN_LENGTH } from '~/stores/SearchStore';

import { DefaultSearchContent } from './DefaultSearchContent';
import { EmptySearchState } from './EmptySearchState';
import { SearchResultsSection } from './SearchResultsSection';

interface RecommendationsItem {
  title: string;
  action?: JSX.Element | undefined;
  items: string[];
  icon: IconTypes;
}

interface SearchDropdownContentProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  recommendationsData: RecommendationsItem[];
  handleSelectRecommendation: (
    value: string,
  ) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleCategoryClick: (
    categoryId: string | number,
  ) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleHistoryReset: () => void;
  handleCloseDropdown: (
    e?: React.MouseEvent<HTMLButtonElement | HTMLDivElement>,
  ) => void;
  filteredSearchResults?: Product[] | null;
  queryString?: string;
  handleStartSearch?: () => void;
  handleExploreCatalog?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const SearchDropdownContent = observer<SearchDropdownContentProps>(
  ({
    recommendationsData,
    handleSelectRecommendation,
    handleCategoryClick,
    handleHistoryReset,
    handleCloseDropdown,
    filteredSearchResults = null,
    queryString = '',
    handleStartSearch,
    handleExploreCatalog,
    className,
    ...props
  }) => {
    const { shopCategories } = useStores();

    const hasSearchQuery = queryString.length >= SEARCH_MIN_LENGTH;
    const isWaitingForResults =
      hasSearchQuery && filteredSearchResults === null;
    const showDefaultContent = !hasSearchQuery || isWaitingForResults;
    const showSearchResults =
      hasSearchQuery &&
      filteredSearchResults !== null &&
      filteredSearchResults.length > 0;
    const showEmptyState =
      hasSearchQuery &&
      filteredSearchResults !== null &&
      filteredSearchResults.length === 0;

    const categories = shopCategories.list
      .reduce((acc: Category[], category) => {
        return [...acc, ...category.subcategory];
      }, [])
      .filter((category) => category.previewImageThumb || category.previewImage)
      .slice(0, 5)
      .map((category) => ({
        id: category.id,
        name: category.name,
        image: category.previewImageThumb || category.previewImage || '',
      }));

    return (
      <div
        {...props}
        className={cn([
          'flex flex-col text-navigation-text-primary',
          showDefaultContent && 'gap-6 pt-2',
          showEmptyState && 'gap-4 px-2 pt-6 pb-12',
          showSearchResults && 'gap-0.5',
          className,
        ])}
      >
        {showSearchResults && (
          <SearchResultsSection
            searchResults={filteredSearchResults}
            handleCloseDropdown={handleCloseDropdown}
            handleStartSearch={handleStartSearch}
          />
        )}

        {showEmptyState && (
          <EmptySearchState
            handleExploreCatalog={handleExploreCatalog}
          />
        )}

        {showDefaultContent && (
          <DefaultSearchContent
            recommendationsData={recommendationsData}
            categories={categories}
            handleSelectRecommendation={handleSelectRecommendation}
            handleCategoryClick={handleCategoryClick}
            handleHistoryReset={handleHistoryReset}
            handleCloseDropdown={handleCloseDropdown}
          />
        )}
      </div>
    );
  },
);
